import { useMemo, useState } from "react";
import { Building2, Lock, Mail, Shield } from "lucide-react";

import { updateProfile, changePassword } from "@/api/auth";
import { useAuth } from "@/auth/useAuth";
import { Alert } from "@/components/Alert";
import { Field } from "@/components/Field";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { Toast } from "@/components/Toast";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";

function initialsOf(fullName, email) {
  return (fullName || email || "U")
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

export function AccountPage() {
  const { user, setUser } = useAuth();

  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileFieldErrors, setProfileFieldErrors] = useState({});

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordFieldErrors, setPasswordFieldErrors] = useState({});

  const [toastMessage, setToastMessage] = useState(null);

  const roleLabel = user?.is_superuser
    ? "Platform Superuser"
    : user?.role || "Staff";

  const organizationLabel = user?.is_superuser
    ? "Homecare SaaS Platform"
    : user?.agency?.name || "—";

  const initials = initialsOf(user?.full_name, user?.email);

  const profileDirty = useMemo(() => {
    return (
      firstName.trim() !== (user?.first_name || "").trim() ||
      lastName.trim() !== (user?.last_name || "").trim() ||
      phone.trim() !== (user?.phone || "").trim()
    );
  }, [firstName, lastName, phone, user]);

  const passwordReady =
    currentPassword.length > 0 &&
    newPassword.length > 0 &&
    confirmPassword.length > 0;

  const discardProfile = () => {
    setFirstName(user?.first_name || "");
    setLastName(user?.last_name || "");
    setPhone(user?.phone || "");
    setProfileError(null);
    setProfileFieldErrors({});
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    if (!profileDirty || savingProfile) return;

    setSavingProfile(true);
    setProfileError(null);
    setProfileFieldErrors({});
    try {
      const updated = await updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
      });
      setUser((prev) => ({ ...prev, ...updated }));
      setFirstName(updated.first_name || "");
      setLastName(updated.last_name || "");
      setPhone(updated.phone || "");
      setToastMessage("Profile saved.");
    } catch (err) {
      if (err.errors || err.fieldErrors) {
        setProfileFieldErrors(err.fieldErrors || err.errors || {});
      } else {
        setProfileError(err.message || "Failed to update profile.");
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    if (!passwordReady || changingPassword) return;

    const nextFieldErrors = {};
    if (newPassword.length < 8) {
      nextFieldErrors.new_password = "Use at least 8 characters.";
    }
    if (newPassword !== confirmPassword) {
      nextFieldErrors.confirm_password = "Passwords do not match.";
    }
    if (Object.keys(nextFieldErrors).length) {
      setPasswordFieldErrors(nextFieldErrors);
      setPasswordError(null);
      return;
    }

    setChangingPassword(true);
    setPasswordError(null);
    setPasswordFieldErrors({});
    try {
      await changePassword({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setToastMessage("Password updated.");
    } catch (err) {
      if (err.errors || err.fieldErrors) {
        const fields = err.fieldErrors || err.errors || {};
        setPasswordFieldErrors({
          current_password: fields.current_password,
          new_password: fields.new_password,
          confirm_password: fields.confirm_password,
        });
      } else {
        setPasswordError(err.message || "Failed to change password.");
      }
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <PageShell>
      <Toast message={toastMessage} onClose={() => setToastMessage(null)} />

      <PageHeader
        title="Account"
        description="Manage your profile details and sign-in security."
      />

      <PageBody className="gap-4">
        <Card className="gap-0 py-4">
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Avatar className="size-12 shrink-0">
              <AvatarFallback className="text-base font-medium">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-base font-semibold tracking-tight">
                  {user?.full_name || "Your account"}
                </h2>
                <Badge variant="outline" className="capitalize shrink-0">
                  {roleLabel}
                </Badge>
              </div>
              <div className="flex flex-col gap-1 text-sm text-muted-foreground sm:flex-row sm:flex-wrap sm:gap-x-4">
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <Mail className="size-3.5 shrink-0" />
                  <span className="truncate">{user?.email}</span>
                </span>
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <Building2 className="size-3.5 shrink-0" />
                  <span className="truncate">{organizationLabel}</span>
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
          <form onSubmit={handleSaveProfile} className="min-w-0">
            <Card className="h-full gap-4 py-4">
              <CardHeader className="px-4 pb-0">
                <CardTitle className="text-base">Profile</CardTitle>
                <CardDescription>
                  Name and phone used across the product. Email is managed by your
                  administrator.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 px-4">
                {profileError ? <Alert type="error">{profileError}</Alert> : null}

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="First name"
                    name="first_name"
                    error={profileFieldErrors.first_name}
                  >
                    {(props) => (
                      <Input
                        {...props}
                        autoComplete="given-name"
                        value={firstName}
                        onChange={(e) => {
                          setFirstName(e.target.value);
                          setProfileFieldErrors((prev) => ({
                            ...prev,
                            first_name: undefined,
                          }));
                        }}
                      />
                    )}
                  </Field>

                  <Field
                    label="Last name"
                    name="last_name"
                    error={profileFieldErrors.last_name}
                  >
                    {(props) => (
                      <Input
                        {...props}
                        autoComplete="family-name"
                        value={lastName}
                        onChange={(e) => {
                          setLastName(e.target.value);
                          setProfileFieldErrors((prev) => ({
                            ...prev,
                            last_name: undefined,
                          }));
                        }}
                      />
                    )}
                  </Field>

                  <Field
                    label="Phone number"
                    name="phone"
                    error={profileFieldErrors.phone}
                    hint="Optional."
                    className="sm:col-span-2"
                  >
                    {(props) => (
                      <Input
                        {...props}
                        type="tel"
                        autoComplete="tel"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value);
                          setProfileFieldErrors((prev) => ({
                            ...prev,
                            phone: undefined,
                          }));
                        }}
                      />
                    )}
                  </Field>

                  <Field
                    label="Email address"
                    name="email"
                    hint="Contact an admin to change email."
                    className="sm:col-span-2"
                  >
                    {(props) => (
                      <Input
                        {...props}
                        type="email"
                        value={user?.email || ""}
                        disabled
                      />
                    )}
                  </Field>
                </div>
              </CardContent>
              <CardFooter className="mt-auto gap-2 border-t border-border px-4 pt-4">
                <Button type="submit" disabled={!profileDirty || savingProfile}>
                  {savingProfile ? "Saving…" : "Save profile"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={!profileDirty || savingProfile}
                  onClick={discardProfile}
                >
                  Discard
                </Button>
              </CardFooter>
            </Card>
          </form>

          <form onSubmit={handleChangePassword} className="min-w-0">
            <Card className="h-full gap-4 py-4">
              <CardHeader className="px-4 pb-0">
                <div className="flex items-start gap-2.5">
                  <div className="mt-0.5 rounded-md bg-muted p-1.5 text-muted-foreground">
                    <Lock className="size-3.5" />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <CardTitle className="text-base">Password</CardTitle>
                    <CardDescription>
                      Choose a strong password you do not reuse. You stay signed
                      in after updating.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 px-4">
                {passwordError ? <Alert type="error">{passwordError}</Alert> : null}

                <div className="grid gap-4">
                  <Field
                    label="Current password"
                    name="current_password"
                    error={passwordFieldErrors.current_password}
                  >
                  {(props) => (
                    <PasswordInput
                      {...props}
                      autoComplete="current-password"
                      value={currentPassword}
                      onChange={(e) => {
                        setCurrentPassword(e.target.value);
                        setPasswordFieldErrors((prev) => ({
                          ...prev,
                          current_password: undefined,
                        }));
                      }}
                    />
                  )}
                </Field>

                  <Field
                    label="New password"
                    name="new_password"
                    error={passwordFieldErrors.new_password}
                    hint="At least 8 characters."
                  >
                    {(props) => (
                      <PasswordInput
                        {...props}
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          setPasswordFieldErrors((prev) => ({
                            ...prev,
                            new_password: undefined,
                            confirm_password: undefined,
                          }));
                        }}
                      />
                    )}
                  </Field>

                  <Field
                    label="Confirm new password"
                    name="confirm_password"
                    error={passwordFieldErrors.confirm_password}
                  >
                    {(props) => (
                      <PasswordInput
                        {...props}
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setPasswordFieldErrors((prev) => ({
                            ...prev,
                            confirm_password: undefined,
                          }));
                        }}
                      />
                    )}
                  </Field>
                </div>

                <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                  <Shield className="mt-0.5 size-3.5 shrink-0" />
                  <p>
                    Never share your password. Staff will never ask for it by
                    email or chat.
                  </p>
                </div>
              </CardContent>
              <CardFooter className="mt-auto gap-2 border-t border-border px-4 pt-4">
                <Button type="submit" disabled={!passwordReady || changingPassword}>
                  {changingPassword ? "Updating…" : "Update password"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={
                    changingPassword ||
                    (!currentPassword && !newPassword && !confirmPassword)
                  }
                  onClick={() => {
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setPasswordError(null);
                    setPasswordFieldErrors({});
                  }}
                >
                  Clear
                </Button>
              </CardFooter>
            </Card>
          </form>
        </div>
      </PageBody>
    </PageShell>
  );
}
